<template>
  <EquipmentRow
    ref="equipmentRow"
    :key="`casing-${casing?.id}`"
    :data-cy="`equipment-row-${casing?.id}`"
  >
    <template #left></template>
    <template #default="{ toggle }">
      <AddRemoveButtons
        :disable-add-above="disableAddAbove"
        :disable-add-below="disableAddBelow"
        :disable-remove="disableRemove"
        :hide-add-above="hideAddAbove"
        :hide-add-below="hideAddBelow"
        :hide-remove="hideRemove"
        :data-cy="`add-remove-buttons-${casing?.id}`"
        @add-below="$emit('add-below')"
        @remove="$emit('remove')"
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :rule-names="
              topCasing ? ['top_casing_obsolete'] : ['bottom_casing_obsolete']
            "
            :data-cy="`equipment-panel-${casing?.id}`"
            @toggle="toggle"
          >
            <template #header>
              <span data-cy="casing-row-panel-header">
                {{ casingRowLabel }}
              </span>
            </template>
            <template v-if="casing" #trigger>
              {{
                computeCasingLabel(
                  casing,
                  getLargeLengthUnit(`${casingLengthLabel}.write.Feet`).value
                    .label === LengthUnits.Meters.label
                )
              }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <CasingSearchView
        :is-admin="false"
        :include-types="[Casings_Type_Enum.Casing, Casings_Type_Enum.Both]"
        :data-cy="`cable-search-view-${casing?.id}`"
        @select="handleCasingSelect($event, toggle)"
      />
    </template>
    <template #right>
      <ConverterInput
        :rule-names="['tubing_length_check']"
        :label="casingLengthLabel"
        :model-value="
          topCasing
            ? wellProperties.top_casing_length_feet
            : wellProperties.bottom_casing_length_feet
        "
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        mode="write"
        :disabled="
          topCasing
            ? !wellProperties.top_casing_id
            : !wellProperties.bottom_casing_id
        "
        :data-cy="`converter-input-${casing?.id}`"
        @update:model-value="
          wellPropertiesForm.update({
            [casingLengthKey]: $event
          })
        "
      />
    </template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { computed, provide } from "vue";
import { Casings_Type_Enum, type Casings } from "gql/api";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { computeCasingLabel } from "@/models/equipment/casings/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import CasingSearchView from "@/components/equipment/casings/CasingSearchView.vue";
import { injectStrict } from "@/utils/utils";
import {
  scenarioKey,
  sizingKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";

const props = defineProps({
  casing: {
    type: Object as PropType<Casings>,
    default: null
  },
  topCasing: {
    type: Boolean,
    default: true
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "remove"): void;
}>();

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);

const casingIdKey = computed(() =>
  props.topCasing ? "top_casing_id" : "bottom_casing_id"
);
const casingRelationKey = computed(() =>
  props.topCasing ? "r_top_casing" : "r_bottom_casing"
);
const casingLengthKey = computed(() =>
  props.topCasing ? "top_casing_length_feet" : "bottom_casing_length_feet"
);
const casingRowLabel = computed(() =>
  props.topCasing ? "Top Casing" : "Bottom Casing"
);
const casingLengthLabel = computed(() =>
  props.topCasing ? "Top Casing Length" : "Bottom Casing Length"
);

const handleCasingSelect = (casing: Casings, toggle: () => void) => {
  toggle();
  wellPropertiesForm.update({
    [casingRelationKey.value]: casing,
    [casingIdKey.value]: casing.id
  });
};

provide("equipment", undefined);
provide(
  "selected-equipment-id",
  computed(() => props.casing?.id)
);
</script>
